import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../../axiosClient';
import { Action, ClientApiUserInput } from '../../interfaces/actions';
import CollapsibleComponent from '../baseComponents/collapsibleComponents';
import DropDown from '../baseComponents/dropdown';

type Actionsrops = {
	client_name: string;
	case_id: string;
	setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
	refresh: boolean;
};

const Actions: React.FC<Actionsrops> = ({ setRefresh, client_name, case_id, refresh }) => {
	const [actions, setActions] = useState<Action[]>([]);
	const [isPopupVisible, setPopupVisible] = useState(false);
	const [selectedApi, setSelectedApi] = useState<Action | null>(null);
	const [selectedInputs, setSelectedInputs] = useState<ClientApiUserInput[]>([]);
	const [clientApiResponse, setClientApiResponse] = useState<string>('');
	const [lastGeneratedTime, setLastGeneratedTime] = useState<number>(0);

	useEffect(() => {
		fetchActionDetails(client_name);
	}, [client_name]);

	const fetchActionDetails = async (client_name: string) => {
		const API_URL = `/api/v1/client_api_definition?client_name=${client_name}`;
		try {
			const response = await apiClient.get(API_URL);
			if (response.status === 200) {
				setActions(Array.isArray(response.data) ? response.data : []);
			}
		} catch (error) {
			alert("error: failed to fetch actions")
			setActions([]);
		}
	};

	const sendActionData = async (case_id: string, data: Record<string, any>): Promise<any> => {
		const API_URL = `/api/v1/client_api_definition/${client_name}/action/${case_id}`;

		try {
			const link = await apiClient.post(API_URL, data);
			if (link.status === 200) {
				return link.data;
			}
		} catch (error: any) {
			if (error.response) {
                return error.response.data.message;
            } else if (error.request) {
                return error.request;
            } else {
                return error.message;
            }
		}
	};

	const handleFetchClick = (action: Action) => {
		fetchLastGeneratedTime();
		setSelectedApi(action);
		setSelectedInputs([]);
		setPopupVisible(true);
	};

	const handleInputChange = (param: string, value: string) => {
		setSelectedInputs((prev) => {
			if (!Array.isArray(prev)) prev = [];
			const index = prev.findIndex((item) => item.param === param);
			if (index !== -1) {
				const updatedInputs = [...prev];
				updatedInputs[index] = { param, param_value: value };
				return updatedInputs;
			} else {
				return [...prev, { param, param_value: value }];
			}
		});
	};

	const invokeClientAction = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const updatedApi = {
				...selectedApi,
				client_api_user_input: [...selectedInputs],
			};
			const clientApiResponse = await sendActionData(case_id, updatedApi);
			if (clientApiResponse) {
				setClientApiResponse(clientApiResponse);
				const timestamp = Date.now();
				setLastGeneratedTime(timestamp);
				localStorage.setItem('lastGeneratedTime', timestamp.toString());
				setRefresh(!refresh);
			}
		} catch (error) {
			alert('Error: Failed to request action');
		}
	};

	const handleCopyToClipboard = () => {
		navigator.clipboard.writeText(clientApiResponse)
			.then(() => {
				alert("API response copied to clipboard.");
			})
			.catch((error) => {
				alert("Failed to copy the response: " + error);
			});
	};

	const fetchLastGeneratedTime = () => {
		const storedTimestamp = localStorage.getItem('lastGeneratedTime');
		if (storedTimestamp) {
			setLastGeneratedTime(parseInt(storedTimestamp, 10));
		}
	};

	return (
		<Box>
			<CollapsibleComponent
				headerText="Actions"
				content={
					<Box>
						{actions.length > 0 ? (
							actions.map((action) => (
								<Box
									key={action.client_api_definition.api_id}
									sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
								>
									<Typography>{action.client_api_definition.api_description}</Typography>
									<Button variant="contained" onClick={() => handleFetchClick(action)}>
										Fetch
									</Button>
								</Box>
							))
						) : (
							<Typography>No action available</Typography>
						)}
					</Box>
				}
			/>

			<Dialog open={isPopupVisible} onClose={() => setPopupVisible(false)} maxWidth="sm" fullWidth>
				<DialogContent>
					<IconButton
						edge="end"
						color="inherit"
						onClick={() => setPopupVisible(false)}
						sx={{ position: 'absolute', top: 10, right: 20 }}
					>
						<CloseIcon />
					</IconButton>

					{selectedApi && selectedApi.client_api_details ? (
						<div>
							<form onSubmit={invokeClientAction}>
								<h3>Action: {selectedApi.client_api_definition.api_description}</h3>
								{selectedApi.client_api_details
									.filter((detail: any) => detail.input_required) // Filter only required inputs
									.map((detail: any) => (
										<div>
											<h4>Enter required details:</h4>
											<div key={detail.api_detail_id}>
												<h4>{detail.param}</h4>
												{detail.input_type === "DROP_DOWN" ? (
													<DropDown
														label={`Select ${detail.param}`}
														selectedValue={selectedInputs.find((item) => item.param === detail.param)?.param_value || []}
														onSelection={(value) => handleInputChange(detail.param, value)}
														items={detail.dropdown_values?.split(";").map((value: any) => ({ value, label: value }))}
														multiple={false}
														keyProp="value"
														valueProp="value"
														displayProp="label"
														required
													/>
												) : (
													<TextField
														label={`Enter ${detail.param}`}
														value={selectedInputs.find((item) => item.param === detail.param)?.param_value || ""}
														onChange={(e) => handleInputChange(detail.param, e.target.value)}
														fullWidth
														variant="outlined"
														margin="normal"
														required
													/>
												)}
											</div>
										</div>
									))}

								{/* Generate Button */}
								<Button
									variant="contained"
									type="submit"
									disabled={Date.now() - lastGeneratedTime < 1 * 60 * 1000}
									sx={{ marginTop: '8px' }}
									style={{
										backgroundColor: Date.now() - lastGeneratedTime < 1 * 60 * 1000 ? "gray" : "",
									}}
								>
									Request
								</Button>
							</form>

							{Date.now() - lastGeneratedTime < 1 * 60 * 1000 && (
								<p style={{ color: 'red', marginTop: '10px' }}>Wait for a minute before re-triggering the action.</p>
							)}

							{clientApiResponse && (
								<div style={{ marginTop: "20px" }}>
									<h4>Response:</h4>
									{
									/* It is fine to inject Html, since it gives us more control on how to show client action message
									on UI and it is safe since we control what the message would be.
									*/
									}
									<p dangerouslySetInnerHTML={{ __html: clientApiResponse }} />
									<Button
										variant="outlined"
										onClick={handleCopyToClipboard}
									>
										Copy
									</Button>
								</div>
							)}
						</div>
					) : (
						<div>No API selected</div>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default Actions;
